import { FC, useState, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useCheckoutSelector, useAppDispatch } from '../reducers/root';
import CheckoutShippingAndDeliveryError from '../components/shared/CheckoutShippingAndDeliveryError';
import CheckoutDeliveryOptions from '../delivery/CheckoutDeliveryOptions';
import { PreloadedDuties } from '../delivery/usePreloadedDuties';
import { trackUserInteractionOnce } from '../helpers/checkoutTracking';
import { useUpdateShipmentQuote } from '../delivery/useUpdateShipmentQuote';
import { shipmentQuoteSelectedAction } from '../actions/commonActions';
import { shipmentNotesErrorAction } from '../actions/checkoutDeliveryActions';

import { CheckoutReturningBuyerDeliveryOptionsView_item$key } from './__generated__/CheckoutReturningBuyerDeliveryOptionsView_item.graphql';
import { CheckoutReturningBuyerDeliveryOptionsView_buyer$key } from './__generated__/CheckoutReturningBuyerDeliveryOptionsView_buyer.graphql';
import { CheckoutReturningBuyerDeliveryOptionsView_transaction$key } from './__generated__/CheckoutReturningBuyerDeliveryOptionsView_transaction.graphql';
import { CheckoutReturningBuyerDeliveryOptionsView_deliveryOptions$key } from './__generated__/CheckoutReturningBuyerDeliveryOptionsView_deliveryOptions.graphql';
import { CheckoutReturningBuyerDeliveryOptionsView_preCheckoutTransaction$key } from './__generated__/CheckoutReturningBuyerDeliveryOptionsView_preCheckoutTransaction.graphql';

const itemFragment = graphql`
    fragment CheckoutReturningBuyerDeliveryOptionsView_item on Item {
        ...CheckoutDeliveryOptions_item
    }
`;

const buyerFragment = graphql`
    fragment CheckoutReturningBuyerDeliveryOptionsView_buyer on User {
        ...CheckoutDeliveryOptions_buyer
    }
`;

const transactionFragment = graphql`
    fragment CheckoutReturningBuyerDeliveryOptionsView_transaction on Transaction {
        ...CheckoutDeliveryOptions_transaction
        ...useUpdateShipmentQuote_transaction
    }
`;

const deliveryOptionsFragment = graphql`
    fragment CheckoutReturningBuyerDeliveryOptionsView_deliveryOptions on ShipmentQuote
    @relay(plural: true) {
        ...CheckoutDeliveryOptions_deliveryOptions
    }
`;

const preCheckoutTransactionFragment = graphql`
    fragment CheckoutReturningBuyerDeliveryOptionsView_preCheckoutTransaction on Transaction {
        ...CheckoutDeliveryOptions_preCheckoutTransaction
    }
`;

type Props = {
    item: CheckoutReturningBuyerDeliveryOptionsView_item$key;
    buyer: CheckoutReturningBuyerDeliveryOptionsView_buyer$key;
    transaction: CheckoutReturningBuyerDeliveryOptionsView_transaction$key;
    deliveryOptions: CheckoutReturningBuyerDeliveryOptionsView_deliveryOptions$key;
    preCheckoutTransaction: CheckoutReturningBuyerDeliveryOptionsView_preCheckoutTransaction$key | null;
    preloadedDuties: PreloadedDuties;
    onStateChange: (state: { isLoading: boolean }) => void;
    isLoading: boolean;
    isComputing: boolean;
};

export const CheckoutReturningBuyerDeliveryOptionsView: FC<Props> = props => {
    const { preloadedDuties, onStateChange, isComputing, isLoading } = props;
    const dispatch = useAppDispatch();

    const hasShippingNotesError = useCheckoutSelector(state => state.ui.hasShippingNotesError);
    const shipmentType = useCheckoutSelector(state => state.transaction.shipmentType);
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);
    const quantity = useCheckoutSelector(state => state.transaction.quantity);
    const [hasShipmentQuoteError, setShipmentQuoteError] = useState(false);
    const isProcessingOffer = useCheckoutSelector(state => state.ui.isProcessingOffer);
    const isReturningBuyer = useCheckoutSelector(state => state.pane.isReturningBuyer);
    const shipmentNotes = useCheckoutSelector(state => state.transaction.shipmentNotes);
    const isCustomQuoteSelected = useCheckoutSelector(
        state => state.delivery.isCustomQuoteSelected
    );

    const item = useFragment(itemFragment, props.item);
    const buyer = useFragment(buyerFragment, props.buyer);
    const transaction = useFragment(transactionFragment, props.transaction);
    const deliveryOptions = useFragment(deliveryOptionsFragment, props.deliveryOptions);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        props.preCheckoutTransaction
    );

    const [updateShipmentQuote] = useUpdateShipmentQuote({ transaction });

    const handleDeliveryOptionSelect = useCallback(
        // remove TSFixMe when `CheckoutDeliveryOptions` is typed
        async (options: $TSFixMe) => {
            onStateChange({ isLoading: true });
            trackUserInteractionOnce();
            let hasError = false;
            const { shipmentType: newShipmentType, shipmentQuoteId: newShipmentQuoteId } = options;
            const quoteUpdateResult = await updateShipmentQuote({
                newShipmentType,
                newShipmentQuoteId,
            });
            hasError = quoteUpdateResult.hasError;
            if (!hasError) {
                dispatch(
                    shipmentQuoteSelectedAction({
                        ...options,
                        isCustomQuoteSelected: options.isCustomQuoteSelected || false,
                    })
                );
            }

            setShipmentQuoteError(hasError);
            onStateChange({ isLoading: false });
        },
        [dispatch, updateShipmentQuote, onStateChange]
    );

    useEffect(() => {
        if (isReturningBuyer && isCustomQuoteSelected && !shipmentNotes) {
            dispatch(shipmentNotesErrorAction());
        }
    }, [isCustomQuoteSelected, shipmentNotes, dispatch, isReturningBuyer]);

    return (
        <>
            {(hasShippingNotesError || hasShipmentQuoteError) && (
                <CheckoutShippingAndDeliveryError />
            )}
            <CheckoutDeliveryOptions
                {...{
                    item,
                    transaction,
                    buyer,
                    preCheckoutTransaction,
                    deliveryOptions,
                    shipmentType,
                    shipmentQuoteId,
                    quantity,
                    preloadedDuties,
                }}
                isComputing={isComputing || isLoading || isProcessingOffer}
                onSelect={handleDeliveryOptionSelect}
            />
        </>
    );
};
